import { Model3D } from "@/plugins/model3D/model-3d"
import { PlacementTypes } from "./types/Graphics.t"
import * as THREE from "three"
import { Mesh, Texture } from "three"
import { SVGSize } from "@/store/modules/utils/util.t"

type CanvasRecord = Partial<Record<PlacementTypes, CanvasRenderer>>

export default class CanvasRenderer {
  canvasEl: HTMLCanvasElement
  context: CanvasRenderingContext2D
  id: string | number
  width: number
  height: number
  img: HTMLImageElement | null
  texture?: Texture
  mesh: Mesh

  public static model3d: Model3D
  public static canvasRecord: CanvasRecord = {}

  public static createCanvas = (targetComponent: PlacementTypes, svgSize: SVGSize, replace = false): void => {
    if (CanvasRenderer.canvasRecord[targetComponent]) {
      if (!replace) return
      CanvasRenderer.removeCanvas(targetComponent)
    }
    new CanvasRenderer(targetComponent, svgSize.width, svgSize.height)
  }

  public static removeCanvas = (targetComponent: PlacementTypes): void => {
    const canvasInstance = CanvasRenderer.canvasRecord[targetComponent]
    if (!canvasInstance) return
    const mesh: Mesh = CanvasRenderer.model3d.rootObj.getObjectByName(targetComponent)
    if (mesh) mesh.visible = false

    // only when debugging (added to DOM).  It will be removed
    canvasInstance!.canvasEl.parentElement
      && canvasInstance!.canvasEl!.parentElement!.removeChild(canvasInstance!.canvasEl)

    canvasInstance && canvasInstance.destroy()
    delete CanvasRenderer.canvasRecord[targetComponent]
  }

  public static createImage(targetComponent: PlacementTypes, svgData: string, svgSize: SVGSize) {
    if (!CanvasRenderer.model3d) throw new Error('CanvasRenderer must be initialized with a 3D scene')

    // create a new instance if it doesnt exist yet
    !CanvasRenderer.canvasRecord[targetComponent] && CanvasRenderer.createCanvas(targetComponent, svgSize)
    CanvasRenderer.canvasRecord[targetComponent]!.createImageInstance(svgData)
  }

  public static destroyAll() {
    for (const prop in CanvasRenderer.canvasRecord) {
      CanvasRenderer.removeCanvas(prop as PlacementTypes)
    }
  }

  constructor(
    id: PlacementTypes,
    width: number,
    height: number,
    debugView = false,
  ) {
    this.id = id
    this.width = width
    this.height = height
    this.canvasEl = document.createElement("canvas")
    this.canvasEl.id = this.id as string
    this.canvasEl.width = width
    this.canvasEl.height = height
    this.context = this.canvasEl.getContext("2d") as CanvasRenderingContext2D
    this.img = null
    this.texture = new THREE.Texture(this.canvasEl)
    this.texture.flipY = false
    this.texture.premultiplyAlpha = true

    CanvasRenderer.canvasRecord[id] = this

    const material = new THREE.MeshStandardMaterial({
      map: this.texture,
      transparent: true,
      opacity: 0.9,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor
    })

    this.mesh = CanvasRenderer.model3d.rootObj.getObjectByName(id)
    this.mesh.material = material
    this.mesh.visible = true
    this.texture.needsUpdate = true

    // render on dom for testing only
    if (debugView) {
      this.canvasEl.style.zIndex = '5000'
      this.canvasEl.style.display = 'block'
      this.canvasEl.style.position = 'absolute'
      this.canvasEl.style.top = '0'
      this.canvasEl.style.right = String(((Object.keys(CanvasRenderer.canvasRecord).length - 1) * 11) + '%')
      this.canvasEl.style.width = '10%'
      this.canvasEl.style.backgroundColor = '#DDDDDD'
      document.body.appendChild(this.canvasEl)
      // end testing only
    }
  }

  draw(imageEl?: HTMLImageElement | undefined): any {
    this.context.clearRect(0, 0, this.canvasEl.width, this.canvasEl.height)

    this.context.beginPath();
    this.context.rect(0, 0, this.canvasEl.width, this.canvasEl.height);
    // fill canvas with white transparent pixels
    this.context.fillStyle = '#ffffff00';
    this.context.fill();
    this.texture!.needsUpdate = true
    if (imageEl || this.img) {
      this.context.drawImage(imageEl || this.img as CanvasImageSource, 0, 0);
    }
    this.img = null
  }

  createImageInstance(svgData: string): void {
    if (!this.img) this.img = document.createElement("img")
    this.img.setAttribute(
      "src",
      "data:image/svg+xml;base64," +
      window.btoa(svgData)
    )
    this.img.onload = () => {
      setTimeout(() => {
        this.draw();
      }, 0);
    }
  }

  destroy(): void {
    this.texture && this.texture.dispose()
    delete this.texture

    // a canvas with w,h of 0 has no memory buffer
    this.canvasEl.width
      = this.canvasEl.height
      = 0
  }

  get canvas(): HTMLCanvasElement {
    return this.canvasEl
  }
}
